import { toDateStr } from '../../../helpers/utcDate';
import useChangeDocDateRegistration from '../mutation/useChangeDocDateRegistration';
import useChangeDocNumber from '../mutation/useChangeDocNumber';
import useChangeDocStatus from '../mutation/useChangeDocStatus';
import useChangeDocTopic from '../mutation/useChangeDocTopic';
import useChangeDocVisibility from '../mutation/useChangeDocVisibility';
import moment from 'moment';
import useUpdateDoneDate from '../mutation/useUpdateDoneDate';
import useChangeDocResponsibleWorker from '../mutation/useChangeDocResponsibleWorker';
import useAddDocsAppointedWorker from '../mutation/useAddDocsAppointedWorker';
import useAddDocsOrgToDoc from '../mutation/useAddDocsOrgToDoc';
import useDeleteDocsOrgToDoc from '../mutation/useDeleteDocsOrgToDoc';
import useDeleteDocsCounterpartyOwner from '../mutation/useDeleteDocsCounterpartyOwner';
import useAddDocsCounterpartyOwner from '../mutation/useAddDocsCounterpartyOwner';
import useAddDocsAddress from '../mutation/useAddDocsAddress';
import useChangeSourceDate from '../mutation/useChangeSourceDate';
import useChangeSourceNumber from '../mutation/useChangeSourceNumber';
import useChangeDocTopicId from '../mutation/useChangeDocTopicId';
import useDeleteDocsAddress from '../mutation/useDeleteDocsAddress';
import useChangeDeadlineDate from '../mutation/useChangeDeadlineDate';
import useChangeTypeDocs from '../mutation/useChangeTypeDocs';
import { docTypes, sourceTypes } from '../constants/documents.constants';
import useChangeCanChange from '../mutation/useChangeCanChange';
import useChangeSubtype from '../mutation/useChangeSubtype';

const isEqualUnordered = (a, b) => {
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.every((v, i) => v === sortedB[i]);
};

const normalize = (v) => String(v).trim();
const normalizeDifferent = (val) => (val == null ? null : val);
const normalizeNumberReq = (v) =>
  v === null || v === undefined || v === '' ? null : Number(v);
const normalizeStrReq = (v) =>
  v === null || v === undefined || v === '' ? null : String(v);

const useChangeDocument = () => {
  const { isLoadingDocVisibility, changeDocVisibility } =
    useChangeDocVisibility();
  const { isLoadingDocNumber, changeDocNumber } = useChangeDocNumber();
  const { isLoadingDocDateRegistration, changeDocDateRegistration } =
    useChangeDocDateRegistration();
  const { isLoadingDocStatus, changeDocStatus } = useChangeDocStatus();
  const { isLoadingDocTopic, changeDocTopic } = useChangeDocTopic();
  const { isLoadingDocTopicId, changeDocTopicId } = useChangeDocTopicId();
  const { isLoadingDocDoneDate, changeDocDoneDate } = useUpdateDoneDate();
  const { isLoadingDocResponsibleWorker, changeDocResponsibleWorker } =
    useChangeDocResponsibleWorker();
  const { isLoadingDocAddAppointedWorker, changeDocAppointedWorker } =
    useAddDocsAppointedWorker();
  const { isLoadingDocsOrgToDoc, addDocsOrgToDoc } = useAddDocsOrgToDoc();
  const { isLoadingDocsOrgToDocDelete, deleteDocsOrgToDoc } =
    useDeleteDocsOrgToDoc();
  const { isLoadingDocsCounterpartyOwnerDelete, deleteDocsCounterpartyOwner } =
    useDeleteDocsCounterpartyOwner();
  const { isLoadingDocsCounterpartyOwnerc, addDocsCounterpartyOwner } =
    useAddDocsCounterpartyOwner();
  const { isLoadingSourceNumber, changeSourceNumber } = useChangeSourceNumber();
  const { isLoadingSourceDate, changeSourceDate } = useChangeSourceDate();
  const { isLoadingDocsAddress, addDocsAddress } = useAddDocsAddress();
  const { isLoadingDeleteDocsAddress, deleteDocsAddress } =
    useDeleteDocsAddress();
  const { isLoadingDocDeadlineDate, changeDocDeadlineDate } =
    useChangeDeadlineDate();
  const { isLoading: isLoadingTypeDocs, mutateAsync: changeDocType } =
    useChangeTypeDocs();
  const { isLoading: isLoadingCanChange, mutateAsync: changeCanChange } =
    useChangeCanChange();
  const { isLoading: isLoadingSubtype, mutateAsync: changeSubtype } =
    useChangeSubtype();

  const isLoading =
    isLoadingDocNumber ||
    isLoadingDocVisibility ||
    isLoadingDocDateRegistration ||
    isLoadingDocStatus ||
    isLoadingDocTopic ||
    isLoadingDocDoneDate ||
    isLoadingDocResponsibleWorker ||
    isLoadingDocAddAppointedWorker ||
    isLoadingDocsOrgToDoc ||
    isLoadingDocsOrgToDocDelete ||
    isLoadingDocsCounterpartyOwnerDelete ||
    isLoadingDocsCounterpartyOwnerc ||
    isLoadingDocsAddress ||
    isLoadingSourceDate ||
    isLoadingSourceNumber ||
    isLoadingDocTopicId ||
    isLoadingDeleteDocsAddress ||
    isLoadingDocDeadlineDate ||
    isLoadingTypeDocs ||
    isLoadingCanChange ||
    isLoadingSubtype;

  const changeDocs = async (oldValue, newValue) => {
    const promises = [];

    const docId = oldValue?.id;

    const isEqualWorkers = isEqualUnordered(
      oldValue.appointedWorkers?.map((item) => item.id) ?? [],
      (newValue.executerIds ?? [])?.filter(
        (item) => item !== null && item !== undefined
      )
    );

    const oldOrgs = oldValue.attachOrg?.map((item) => item.orgId) ?? [];
    const newOrgs = newValue.orgId ?? [];

    const oldSetOrg = new Set(oldOrgs);
    const newSetOrg = new Set(newOrgs);

    const toRemoveOrg = oldOrgs.filter((id) => !newSetOrg.has(id));
    const toAddOrg = newOrgs.filter((id) => !oldSetOrg.has(id));

    const isEqualOrgs = toRemoveOrg.length === 0 && toAddOrg.length === 0;

    const oldAddresses = oldValue.adresses
      ? oldValue.adresses?.map((address) => address.addressId)
      : [];
    const newAddresses = (newValue.addressIds || [])?.filter(
      (item) => item !== null && item !== undefined
    );

    const isEqualAddresses =
      oldAddresses.length === 0 && newAddresses.length === 0;

    const oldAddressesMap = new Map(
      oldAddresses?.map((addressId) => [addressId, addressId])
    );
    const newAddressesMap = new Map(
      newAddresses?.map((addressId) => [addressId, addressId])
    );

    const addedAddresses = newAddresses.filter(
      (addressId) => !oldAddressesMap.has(addressId)
    );

    const removedAddresses = oldAddresses.filter(
      (addressId) => !newAddressesMap.has(addressId)
    );

    const oldCounterpartyOwners = (oldValue.counterpartyOwners ?? []).map(
      (item) => ({
        name: normalize(item.clientName),
        id: item.id,
      })
    );

    const newCounterpartyOwners = (newValue.custNames ?? [])
      .map((name) => normalize(name ?? ''))
      .filter((name) => name.trim().length > 0)
      .filter((item) => item !== null && item !== undefined);

    const newSet = new Set(newCounterpartyOwners);

    const toRemoveCounterpartyOwners = oldCounterpartyOwners
      .filter((item) => !newSet.has(item.name))
      .map((item) => item.id);

    const oldNamesSet = new Set(oldCounterpartyOwners.map((item) => item.name));
    const toAddCounterpartyOwners = newCounterpartyOwners.filter(
      (name) => !oldNamesSet.has(name)
    );

    const isEqualCounterpartyOwners =
      toRemoveCounterpartyOwners.length === 0 &&
      toAddCounterpartyOwners.length === 0;

    if (oldValue.documentVisibility !== newValue.documentVisibility) {
      promises.push(
        changeDocVisibility({
          docId,
          visibility: newValue.documentVisibility,
        })
      );
    }

    if (
      (!!oldValue?.docNumber ? oldValue?.docNumber?.split('/')[0] : null) !==
      newValue.DocNumber
    ) {
      promises.push(
        changeDocNumber({
          docId,
          docNumber: newValue.DocNumber,
        })
      );
    }

    const oldTopic = normalizeStrReq(oldValue.topic || '');
    const newTopic = normalizeStrReq(newValue.topic || '');

    const oldTopicId = normalizeNumberReq(oldValue.topicId);
    const newTopicId = normalizeNumberReq(newValue.topicId);

    if (newValue.topic) {
      if (oldTopic !== newTopic) {
        promises.push(
          changeDocTopic({
            docId,
            topic: newValue.topic,
          })
        );
      }
    }
    if (newValue.topicId) {
      if (oldTopicId !== newTopicId) {
        promises.push(
          changeDocTopicId({
            docId,
            topicId: newValue.topicId,
          })
        );
      }
    }

    if (!moment(oldValue.docDate).isSame(newValue.docDate, 'day')) {
      promises.push(
        changeDocDateRegistration({
          docId,
          registrationDate: toDateStr(newValue.docDate),
        })
      );
    }

    if (!moment(oldValue.deadlineDate).isSame(newValue.DeadlineDate, 'day')) {
      promises.push(
        changeDocDeadlineDate({
          docId,
          deadlineDate: toDateStr(newValue.DeadlineDate),
        })
      );
    }

    if (
      normalizeDifferent(oldValue.sourceNumber) !==
      normalizeDifferent(newValue.sourceNumber)
    ) {
      promises.push(
        changeSourceNumber({
          docId,
          sourceNumber: newValue.sourceNumber,
        })
      );
    }

    if (oldValue.sourceDate && newValue.sourceDate) {
      if (!moment(oldValue.sourceDate).isSame(newValue.sourceDate, 'day')) {
        promises.push(
          changeSourceDate({
            docId,
            sourceDate: toDateStr(newValue.sourceDate),
          })
        );
      }
    } else if (
      normalizeDifferent(oldValue.sourceDate) !==
      normalizeDifferent(newValue.sourceDate)
    ) {
      promises.push(
        changeSourceDate({
          docId,
          sourceDate: newValue.sourceDate
            ? toDateStr(newValue.sourceDate)
            : null,
        })
      );
    }

    if (oldValue.status.id !== newValue.statusId) {
      promises.push(
        changeDocStatus({
          docId,
          statusId: newValue.statusId,
        })
      );
    }

    if (oldValue.doneDate && newValue.doneDate) {
      if (!moment(oldValue.doneDate).isSame(newValue.doneDate, 'day')) {
        promises.push(
          changeDocDoneDate({
            docId,
            doneDate: toDateStr(newValue.doneDate),
          })
        );
      }
    } else if (
      normalizeDifferent(oldValue.doneDate) !==
      normalizeDifferent(newValue.doneDate)
    ) {
      promises.push(
        changeDocDoneDate({
          docId,
          doneDate: newValue.doneDate ? toDateStr(newValue.doneDate) : null,
        })
      );
    }

    if (
      (!!oldValue?.responsibleWorker?.id
        ? oldValue.responsibleWorker.id
        : null) !== newValue.ResponsibleWorkerId
    ) {
      promises.push(
        changeDocResponsibleWorker({
          docId,
          respWorkerId: newValue.ResponsibleWorkerId,
        })
      );
    }

    if (!isEqualWorkers) {
      promises.push(
        changeDocAppointedWorker({
          docId,
          WorkerIds: newValue.executerIds,
        })
      );
    }

    if (!isEqualOrgs) {
      if (toAddOrg.length > 0) {
        toAddOrg.map((orgId) =>
          promises.push(addDocsOrgToDoc({ docId, orgId }))
        );
      }
      if (toRemoveOrg.length > 0) {
        toRemoveOrg.map((orgId) =>
          promises.push(deleteDocsOrgToDoc({ docId, orgId }))
        );
      }
    }

    if (!isEqualAddresses) {
      if (addedAddresses.length > 0) {
        addedAddresses.map((addressId) =>
          promises.push(addDocsAddress({ docId, addressId }))
        );
      }
      if (removedAddresses.length > 0) {
        removedAddresses.forEach((addressId) => {
          const address = oldValue?.adresses.find(
            (adress) => adress?.addressId === addressId
          );

          if (!!address) {
            promises.push(deleteDocsAddress({ docId, addressId: address?.id }));
          }
        });
      }
    }

    if (!isEqualCounterpartyOwners) {
      if (toAddCounterpartyOwners.length > 0) {
        toAddCounterpartyOwners.map((clientName) =>
          promises.push(addDocsCounterpartyOwner({ docId, clientName }))
        );
      }
      if (toRemoveCounterpartyOwners.length > 0) {
        toRemoveCounterpartyOwners.map((custId) =>
          promises.push(deleteDocsCounterpartyOwner(custId))
        );
      }
    }

    if (
      oldValue.canChange &&
      oldValue?.sourceId === sourceTypes.mail &&
      oldValue?.type?.id !== newValue?.typeId
    ) {
      promises.push(
        changeDocType({ docId: oldValue.id, typeId: newValue?.typeId })
      );
    }

    if (
      oldValue.canChange &&
      oldValue?.sourceId === sourceTypes.mail &&
      newValue?.typeId === docTypes.incoming &&
      oldValue.subtype?.id !== newValue.subTypeId
    ) {
      promises.push(
        changeSubtype({
          docId: oldValue.id,
          ...(!!newValue?.subTypeId ? { subTypeId: newValue?.subTypeId } : {}),
        })
      );
    }
    await Promise.all(promises);

    if (oldValue.canChange) {
      await changeCanChange({ docId: oldValue.id, canChange: false });
    }
  };

  return { changeDocs, isLoadingChangeDoc: isLoading };
};

export default useChangeDocument;
